import { PatientFile } from "./patientFile"

interface RawDataObject {
    obj_id?: number
    id?: number
    obj_name?: string
    obj_type?: string
    filesize?: number
    start_time?: number | string | null
    end_time?: number | string | null
    _start_time?: number | string | null
    _end_time?: number | string | null
    timezone?: string | null
    file_exists?: boolean
    is_reviewable?: boolean
}

interface PatientLike {
    data_objects?: RawDataObject[]
    data_file_ids?: number[]
    data_file_name?: string[]
    data_file_exists?: boolean[]
    data_file_is_reviewable?: boolean[]
    data_file_sizes?: number[]
    data_file_start_times?: Array<number | string | null>
    data_file_end_times?: Array<number | string | null>
}

const startTimeMs = (value: PatientFile["start_time"]): number => {
    if (value === null || value === undefined) {
        return -Infinity
    }
    const ms = new Date(value).getTime()
    return Number.isFinite(ms) ? ms : -Infinity
}

// Newest recording first; files with no start time sort to the bottom.
const byNewestFirst = (a: PatientFile, b: PatientFile): number =>
    startTimeMs(b.start_time) - startTimeMs(a.start_time)

const fileTypeRank = (file: PatientFile): number => (file.obj_type === "EDF" ? 1 : 0)

// CNS archives (and untyped clinical rows) before EDF files, newest first within each group.
const byArchivesThenEdfNewestFirst = (a: PatientFile, b: PatientFile): number =>
    fileTypeRank(a) - fileTypeRank(b) || byNewestFirst(a, b)

const fromDataObjects = (dataObjects: RawDataObject[]): PatientFile[] =>
    dataObjects.map(raw => {
        const objId = raw.obj_id ?? raw.id
        return {
            obj_id: objId,
            obj_name: raw.obj_name || `data_object #${objId}`,
            obj_type: raw.obj_type,
            filesize: Number(raw.filesize || 0),
            start_time: raw.start_time || raw._start_time || null,
            end_time: raw.end_time || raw._end_time || null,
            timezone: raw.timezone ?? null,
            file_exists: raw.file_exists,
            is_reviewable: raw.is_reviewable,
        }
    })

const fromClinicalArrays = (patient: PatientLike): PatientFile[] => {
    const ids = Array.isArray(patient.data_file_ids) ? patient.data_file_ids : []
    const names = Array.isArray(patient.data_file_name) ? patient.data_file_name : []
    const exists = Array.isArray(patient.data_file_exists) ? patient.data_file_exists : []
    const reviewable = Array.isArray(patient.data_file_is_reviewable) ? patient.data_file_is_reviewable : []
    const sizes = Array.isArray(patient.data_file_sizes) ? patient.data_file_sizes : []
    const starts = Array.isArray(patient.data_file_start_times) ? patient.data_file_start_times : []
    const ends = Array.isArray(patient.data_file_end_times) ? patient.data_file_end_times : []

    return ids.map((id, i) => ({
        obj_id: id,
        obj_name: names[i] || `data_object #${id}`,
        filesize: Number(sizes[i] || 0),
        start_time: starts[i] ?? null,
        end_time: ends[i] ?? null,
        file_exists: exists[i] !== false,
        is_reviewable: reviewable[i] !== false,
    }))
}

/**
 * Produce a PatientFile[] from either patient shape — the `data_objects` array
 * (obj_* fields) or the clinical parallel arrays (data_file_*). Keeps every file
 * with its file_exists flag intact and sorts CNS archives before EDF files,
 * newest recording first within each group. Callers
 * that need export eligibility apply `filterExportableFiles` on top of this.
 */
export function normalizePatientFiles(patient: PatientLike | null | undefined): PatientFile[] {
    if (!patient) {
        return []
    }

    const dataObjects = Array.isArray(patient.data_objects) ? patient.data_objects : []
    const files = dataObjects.length > 0 ? fromDataObjects(dataObjects) : fromClinicalArrays(patient)
    return files.sort(byArchivesThenEdfNewestFirst)
}

/**
 * Export-specific eligibility filter (not baked into normalize on purpose):
 *  - data_objects rows carry obj_type + filesize, so require a CNS archive that
 *    exists on disk with real bytes.
 *  - clinical rows carry neither (obj_type undefined), so only require that the
 *    file exists; POST /v1/exports validates obj_type/path server-side.
 */
export function filterExportableFiles(files: PatientFile[]): PatientFile[] {
    return files.filter(file => {
        if (file.file_exists === false) {
            return false
        }
        if (file.obj_type === undefined) {
            return true
        }
        return file.obj_type === "CNS" && Number(file.filesize || 0) > 0
    })
}
